// A2's 30-day sparkline. Analytics-local rather than a `ui/` primitive: the one
// rule that makes it correct here is a rule about this screen's data, not about
// charts, and pushing it into the shared library would invite a second caller
// to pass a pre-zeroed series and lose it.
//
// THE RULE: a `null` point is a GAP, never a zero. A look-up rate is a ratio,
// so a day no visit opened the term has no rate at all — and a line drawn flat
// along the axis across those days is the single most convincing way to claim
// "nobody opened this" about a period in which nothing was measured. Nulls
// therefore break the line into segments and the caller states, in words, how
// many days were measurable.
//
// No axes, no gridlines, no tooltip. A sparkline's job is the shape; the
// numbers beside it are the numbers.

const WIDTH = 96;
const HEIGHT = 24;
const PAD = 2;

export type SparklineProps = {
  /** Oldest first. `null` where the day was not measurable. */
  values: Array<number | null>;
  /** Read aloud in place of the drawing — the shape is not available to a
   *  screen reader, so the sentence has to carry it. */
  label: string;
};

/** Contiguous runs of measured points, as `[x, y]` pairs in SVG space.
 *
 * Exported for the test: the gap behaviour is the whole contract and asserting
 * it through rendered SVG path strings would pin the drawing, not the rule.
 */
export function segments(values: Array<number | null>): Array<Array<[number, number]>> {
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) return [];
  // Baseline at zero, not at the minimum: these are rates whose distance from
  // zero is the point, and a min-anchored line exaggerates a flat series.
  const top = Math.max(...present, 0) || 1;
  const step = values.length > 1 ? (WIDTH - PAD * 2) / (values.length - 1) : 0;

  const runs: Array<Array<[number, number]>> = [];
  let run: Array<[number, number]> = [];
  values.forEach((value, index) => {
    if (value === null) {
      if (run.length > 0) runs.push(run);
      run = [];
      return;
    }
    run.push([
      PAD + index * step,
      HEIGHT - PAD - (value / top) * (HEIGHT - PAD * 2),
    ]);
  });
  if (run.length > 0) runs.push(run);
  return runs;
}

export function Sparkline({ values, label }: SparklineProps) {
  const runs = segments(values);

  return (
    <svg
      className="maisight-analytics-sparkline"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width={WIDTH}
      height={HEIGHT}
      role="img"
      aria-label={label}
      focusable="false"
    >
      {runs.map((run) => (
        // A single measured day between two gaps has no line to draw, so it
        // gets a dot. Dropping it would hide the only reading there is.
        <g key={`${run[0][0]}`}>
          {run.length === 1 ? (
            <circle cx={run[0][0]} cy={run[0][1]} r={1.5} />
          ) : (
            <polyline points={run.map(([x, y]) => `${x},${y}`).join(" ")} />
          )}
        </g>
      ))}
    </svg>
  );
}
